<template>
  <app-page-subsection
    v-if="displayProgramSection"
    id="program"
    :title="{ tag: 'h3', text: $t('course.page.program_title') }"
  >
    <course-page-program-content
      v-if="displayProgramContent"
      :course="course"
      data-cy-program-content
      :next-session="nextSession"
    />

    <div
      v-else
      class="bg-accent-100 text-accent-950 grid place-content-center rounded-xl p-6 text-center md:p-8"
      data-cy-program-placeholder
    >
      <div class="mx-auto mb-6 flex items-end gap-4">
        <app-image
          v-motion
          alt=""
          class="h-[120px] sm:h-auto"
          :initial="{ opacity: 0, x: -10 }"
          src="/images/illustrations/download_program.svg"
          :visible-once="{
            opacity: 1,
            x: 0,
            transition: {
              delay: 300,
              duration: 200,
              type: 'spring',
              stiffness: 250,
              damping: 25,
              mass: 0.5,
            },
          }"
        />

        <ul class="mb-6 hidden flex-col items-start gap-2 sm:flex">
          <li
            v-for="(item, index) in featuresList"
            :key="item.label"
            v-motion
            class="bg-accent-950 relative flex items-center gap-2 rounded px-2 py-1 text-sm font-semibold text-white"
            :class="{
              '-left-8': index === 1,
              '-left-16': index === 0,
            }"
            :initial="{ opacity: 0, x: 10 }"
            :visibleOnce="{
              opacity: 1,
              x: 0,
              transition: {
                delay: 200 + index * 50,
                duration: 200,
                type: 'spring',
                stiffness: 250,
                damping: 25,
                mass: 0.5,
              },
            }"
          >
            <app-icon :icon="item.icon" :size="18" />
            {{ item.label }}
          </li>
        </ul>
      </div>

      <p class="font-title max-w-[600px] text-balance text-2xl/normal">
        Vous voulez en savoir plus sur cette formation ?
      </p>

      <div class="pt-8">
        <course-program-download-cta
          :course="course"
          origin="course"
          size="lg"
        />
      </div>
    </div>
  </app-page-subsection>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";

import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";
import CoursePageProgramContent from "~/pages/courses/[slug]/_includes/components/program/course-page-program-content.vue";
import {
  useCoursePageProgramDownload,
  useCoursePageProgramDownloadOnPageInit,
} from "~/pages/courses/[slug]/_includes/composables/course-page-program-download.hook";

const properties = defineProps<{
  course: CoursePageView;
  nextSession?: CourseSessionView;
}>();

useCoursePageProgramDownloadOnPageInit(properties.course);

const featuresList = computed<{ label: I18nMessage; icon: string }[]>(() => {
  return [
    { label: "Partage d’expérience", icon: "ph:user-sound" },
    { label: "Cas pratiques", icon: "ph:pencil-line" },
    { label: "Théorie & méthodes", icon: "ph:atom" },
  ];
});

const { can } = useCurrentUser();
const { isRegisteredToCourseSession } = useLearnerProfile();
const { hasAlreadyDownloadedProgram } = useCoursePageProgramDownload(
  properties.course,
);

const displayProgramSection = computed(() => {
  return properties.course.has_program || !properties.course.registration_token;
});

const displayProgramContent = computed(() => {
  return (
    properties.course.has_program &&
    (can("backoffice:access") ||
      hasAlreadyDownloadedProgram.value ||
      properties.course.registration_token ||
      (properties.nextSession &&
        isRegisteredToCourseSession(properties.nextSession.id)))
  );
});
</script>
